import { useEffect } from 'react'
import { RouterProvider } from 'react-router-dom'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { store, persistor } from './store'
import { router } from './router'
import { AppUpdateNotice } from './components/pwa/AppUpdateNotice'
import { YmClickTracker } from './components/YmClickTracker'
import { detectIOS, detectStandalone } from './features/pwa/platform'
import { ThemeContext } from './features/theme/themeContext'
import { useThemeDetect } from './hooks/useThemeDetect'
import { reachPwaGoal } from './ym'

const IOS_INSTALL_DETECTED_KEY = 'pg19_pwa_ios_installed_marked'

/**
 * Метрика PWA install на iOS.
 *
 * iOS Safari не даёт `beforeinstallprompt` / `appinstalled`. Факт установки
 * видно только post-factum: юзер открыл приложение из иконки на домашнем
 * экране → `display-mode: standalone` становится true. Отмечаем один раз в
 * localStorage, чтобы не слать событие на каждый запуск.
 */
function useIosPwaInstallDetection(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!detectIOS()) return
    if (!detectStandalone()) return
    if (localStorage.getItem(IOS_INSTALL_DETECTED_KEY)) return
    try {
      localStorage.setItem(IOS_INSTALL_DETECTED_KEY, '1')
    } catch {
      // quota exceeded / private mode — всё равно шлём event один раз за
      // сессию, в худшем случае получим N дублей по количеству релоадов.
    }
    reachPwaGoal('pwa_installed', { source: 'ios_standalone_detected' })
  }, [])
}

export default function App() {
  useIosPwaInstallDetection()
  const theme = useThemeDetect()

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeContext.Provider value={theme}>
          <YmClickTracker />
          <AppUpdateNotice />
          <RouterProvider router={router} />
        </ThemeContext.Provider>
      </PersistGate>
    </Provider>
  )
}
